/* Sticky Add to cart bar (snippets/sticky-add-to-cart.liquid, assets/sticky-add-to-cart.css), product page, phones only
   (the CSS hides it on wider screens). The bar is just a button.

   - Shows (data-visible) while the real Add to cart button is out of the viewport, hides while it is on screen.
   - A tap clicks the real button, so the form, quantity, errors, loading state and cart drawer behave as always.
   - Copies the real button's label / disabled / loading state, so variant changes (e.g. "Sold out") show up here.
     Dawn updates those in place (product-info.js), so one MutationObserver on the product info is enough.
   - If adding fails (the real form shows an error next to its own button, off screen), the page scrolls to that message.
   - Puts sh-sticky-atc-visible on <body> while it shows: the CSS uses it to hide the mobile bottom nav. */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('[data-sticky-button]');
        this.label = this.querySelector('[data-sticky-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.original = null;
        this.clickedAt = 0;
        this.frame = 0;

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        // Show the bar while the real button is out of view
        this.intersection = new IntersectionObserver(([entry]) => this.setVisible(!entry.isIntersecting));

        // Follow the real button and the error message
        this.mutations = new MutationObserver(() => this.scheduleSync());
        const info = document.getElementById(`MainProduct-${this.sectionId}`);
        if (info) {
          this.mutations.observe(info, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class'],
          });
        }

        this.sync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.intersection?.disconnect();
        this.mutations?.disconnect();
        cancelAnimationFrame(this.frame);
        document.body.classList.remove('sh-sticky-atc-visible');
      }

      get originalButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      scheduleSync() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }

      setVisible(visible) {
        this.toggleAttribute('data-visible', visible);
        document.body.classList.toggle('sh-sticky-atc-visible', visible);
      }

      sync() {
        const original = this.originalButton;

        // Watch whichever button is on the page now
        if (original !== this.original) {
          this.intersection.disconnect();
          this.original = original;
          if (original) this.intersection.observe(original);
          else this.setVisible(false);
        }

        if (original) {
          const text = original.querySelector('span')?.textContent.trim();
          if (text && this.label.textContent !== text) this.label.textContent = text;

          this.button.disabled = original.hasAttribute('disabled');
          this.toggleAria(original.getAttribute('aria-disabled'));
          this.button.classList.toggle('loading', original.classList.contains('loading'));
          this.spinner?.classList.toggle('hidden', !original.classList.contains('loading'));
        }

        // After a tap on this button: if the real form shows an error, bring it into view
        if (this.clickedAt) {
          const error = document.querySelector(`#MainProduct-${this.sectionId} .product-form__error-message-wrapper`);
          if (Date.now() - this.clickedAt > 10000) {
            this.clickedAt = 0;
          } else if (error && !error.hidden) {
            this.clickedAt = 0;
            error.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }

      toggleAria(value) {
        if (value === null) this.button.removeAttribute('aria-disabled');
        else this.button.setAttribute('aria-disabled', value);
      }

      onClick() {
        const original = this.originalButton;
        if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true') return;
        this.clickedAt = Date.now();
        original.click();
      }
    }
  );
}
